import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BASE, authHeaders, jsonHeaders, useApi } from './api';
import { WIDGET_COMPONENTS } from './widgetRegistry';
import FestiveBanner from './festive/FestiveBanner';
import { activeFestival, isoOf } from './festive/festivals.config';
import { visibleWorkspaceModules, savePins, TileIcon, PinIcon } from '../pages/Workspace';

// Widget grid: 12 columns, every widget picks a width (4 / 6 / 8 / 12). Which
// widgets a user may use is decided by their role (Settings → User Roles →
// Dashboard Widgets) and enforced by each widget's API route; the arrangement
// (order, hidden, sizes) is the user's own and is saved per user.

const CSS = `
.cxd-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:16px;align-items:start}
.cxd-s4{grid-column:span 4}.cxd-s6{grid-column:span 6}.cxd-s8{grid-column:span 8}.cxd-s12{grid-column:span 12}
@media(max-width:1100px){.cxd-s4,.cxd-s6,.cxd-s8{grid-column:span 6}}
@media(max-width:700px){.cxd-s4,.cxd-s6,.cxd-s8,.cxd-s12{grid-column:span 12}}
.cxd-w{border-radius:16px;min-width:0;transition:box-shadow .15s,opacity .15s}
.cxd-edit .cxd-w{outline:2px dashed rgba(99,102,241,.45);outline-offset:-2px;cursor:grab}
.cxd-w.cxd-drag{opacity:.4}.cxd-w.cxd-over{box-shadow:0 0 0 3px #6366f1}
.cxd-pins{display:grid;grid-template-columns:repeat(auto-fill,minmax(210px,1fr));gap:12px}
.cxd-pin{transition:border-color .15s}.cxd-pin:hover,.cxd-pin:focus-visible{border-color:#3b82f6!important}
.cxd-drawer{position:fixed;top:0;right:0;bottom:0;width:380px;max-width:94vw;z-index:1001;display:flex;flex-direction:column;box-shadow:-10px 0 40px rgba(0,0,0,.25)}
`;

// Full permutation of the user's allowed widgets (hidden ones keep their place).
function normalize(meta, layout) {
  const allowed = meta.widgets.filter(w => w.allowed).map(w => w.key);
  const base = layout && layout.order && layout.order.length ? layout.order : meta.defaultOrder;
  const order = [...base.filter(k => allowed.includes(k)), ...meta.defaultOrder.filter(k => allowed.includes(k) && !base.includes(k))];
  allowed.forEach(k => { if (!order.includes(k)) order.push(k); });
  return {
    order,
    hidden: ((layout && layout.hidden) || []).filter(k => allowed.includes(k)),
    sizes: (layout && layout.sizes) || {},
  };
}

export default function DashboardHome({ setActivePage, darkMode, T, can }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'staff';

  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ order: [], hidden: [], sizes: {} });
  const [editing, setEditing] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overKey, setOverKey] = useState(null);
  const dragKey = useRef(null);
  const festive = useApi('/api/festive-settings');
  const [holidayNotice, setHolidayNotice] = useState(null); // { when: 'today'|'tomorrow', name }
  const [pins, setPins] = useState([]); // workspace modules pinned from the Workspace page
  const [pinError, setPinError] = useState('');

  const loadMeta = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/api/dashboard-widgets/me`, { headers: authHeaders() });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error((j && j.message) || `HTTP ${r.status}`);
      setMeta(j);
      setDraft(normalize(j, j.layout));
      setPins((j.layout && j.layout.pins) || []);
      setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Today-is-holiday / tomorrow-is-holiday notice, from the firm's own Holidays List.
  useEffect(() => {
    const today = new Date();
    const toISO = d => d.toISOString().slice(0, 10);
    const todayISO = toISO(today);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowISO = toISO(tomorrow);
    const years = new Set([today.getFullYear(), tomorrow.getFullYear()]);
    (async () => {
      try {
        const lists = await Promise.all([...years].map(y =>
          fetch(`${BASE}/api/holidays?year=${y}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : [])
        ));
        const all = lists.flat();
        const todayH = all.find(h => h.holiday_date?.slice(0, 10) === todayISO);
        const tomorrowH = all.find(h => h.holiday_date?.slice(0, 10) === tomorrowISO);
        if (todayH) setHolidayNotice({ when: 'today', name: todayH.name });
        else if (tomorrowH) setHolidayNotice({ when: 'tomorrow', name: tomorrowH.name });
        else setHolidayNotice(null);
      } catch { /* non-critical */ }
    })();
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      await fetch(`${BASE}/api/dashboard-widgets/layout`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(next) });
    } catch (e) { /* layout is cosmetic; the next Done will retry */ } finally { setSaving(false); }
  };
  const finishEditing = async () => { setEditing(false); setDrawer(false); await persist(draft); };
  const restoreDefault = async () => {
    await fetch(`${BASE}/api/dashboard-widgets/layout`, { method: 'DELETE', headers: authHeaders() }).catch(() => {});
    setDraft(normalize(meta, null));
  };

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const todayLabel = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const card = { background: T.cardBg, border: `1px solid ${T.cardBorder}`, boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(15,31,69,0.06)' };
  const btn = (primary) => ({ fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${primary ? '#6366f1' : T.border}`, background: primary ? '#6366f1' : T.cardBg, color: primary ? '#fff' : T.text });

  if (error) return <div style={{ padding: 32, color: T.text2, fontSize: 14 }}>Could not load your dashboard. {error}</div>;
  if (!meta) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${T.border}`, borderTopColor: '#f97316', animation: 'cxd-spin 0.8s linear infinite' }} />
        <style>{'@keyframes cxd-spin{to{transform:rotate(360deg)}}'}</style>
        <p style={{ color: T.text3, fontSize: 13 }}>Loading dashboard…</p>
      </div>
    );
  }

  const byKey = Object.fromEntries(meta.widgets.map(w => [w.key, w]));
  const visible = draft.order.filter(k => !draft.hidden.includes(k));
  const hiddenAllowed = draft.order.filter(k => draft.hidden.includes(k));
  const locked = meta.widgets.filter(w => !w.allowed);
  const sizeOf = k => draft.sizes[k] || byKey[k].size;

  const setOrder = (fromKey, toKey) => {
    if (!fromKey || fromKey === toKey) return;
    setDraft(d => {
      const order = d.order.filter(k => k !== fromKey);
      order.splice(order.indexOf(toKey), 0, fromKey);
      return { ...d, order };
    });
  };
  const cycleSize = k => setDraft(d => {
    const sizes = byKey[k].sizes, cur = d.sizes[k] || byKey[k].size;
    return { ...d, sizes: { ...d.sizes, [k]: sizes[(sizes.indexOf(cur) + 1) % sizes.length] } };
  });
  const hide = k => setDraft(d => ({ ...d, hidden: [...d.hidden, k] }));
  const show = k => setDraft(d => ({ ...d, hidden: d.hidden.filter(x => x !== k) }));

  const unpin = async (key) => {
    const prev = pins;
    const next = prev.filter(k => k !== key);
    setPins(next);
    setPinError('');
    try { await savePins(next, prev); } catch (e) { setPins(prev); setPinError('Could not unpin. Please try again.'); }
  };
  // A pin stays saved if access is later removed, but only shows while the user may open the module.
  const allowedModules = visibleWorkspaceModules(can);
  const pinnedModules = pins.map(k => allowedModules.find(m => m.key === k)).filter(Boolean);

  const fs = festive.data;
  const festival = fs ? activeFestival(isoOf(), fs) : null;

  return (
    <div className={editing ? 'cxd-edit' : ''} style={{ padding: '20px 24px 32px', background: T.bg, minHeight: '100%' }}>
      <style>{CSS}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: 0 }}>
            {greeting}, <span style={{ color: '#f97316' }}>{(user.full_name || 'there').split(' ')[0]}</span> 👋
          </h1>
          <p style={{ fontSize: 12, color: T.text3, margin: '3px 0 0' }}>{todayLabel}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {[['+ Lead', 'crm', '#6366f1'], ['+ Client', 'clients', '#3b82f6'], ['+ Task', 'tasks', '#f59e0b']].map(([label, page, color]) => (
            <button key={label} onClick={() => setActivePage(page)}
              style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${color}`, background: 'transparent', color, cursor: 'pointer' }}>{label}</button>
          ))}
          <button style={btn(false)} onClick={() => setDrawer(true)}>＋ Add widget</button>
          <button style={btn(editing)} onClick={() => (editing ? finishEditing() : setEditing(true))}>{editing ? (saving ? 'Saving…' : '✓ Done') : '✎ Customize'}</button>
        </div>
      </div>

      {editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14, padding: '10px 14px', borderRadius: 12, border: '1px dashed #6366f1', background: darkMode ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.07)', fontSize: 12.5, color: T.text }}>
          <b>Customizing your dashboard.</b> Drag a widget to move it, use S / M / L to resize, ✕ to hide.
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button style={btn(false)} onClick={restoreDefault}>Restore default layout</button>
            <button style={btn(true)} onClick={finishEditing}>Done</button>
          </span>
        </div>
      )}

      {holidayNotice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 16px', borderRadius: 12, border: `1px solid ${darkMode ? 'rgba(249,115,22,0.3)' : '#fed7aa'}`, background: darkMode ? 'rgba(249,115,22,0.1)' : '#fff7ed', fontSize: 13, color: T.text }}>
          <span style={{ fontSize: 16 }}>🎉</span>
          {holidayNotice.when === 'today'
            ? <span><b>Today is a holiday</b> — {holidayNotice.name}</span>
            : <span><b>Tomorrow is a holiday</b> — {holidayNotice.name}. Plan accordingly.</span>}
        </div>
      )}

      {festival && (
        <div style={{ marginBottom: 16 }}>
          <FestiveBanner festival={festival} iso={isoOf()} signOff={fs.sign_off} note={(fs.notes || {})[festival.id]} T={T} darkMode={darkMode} />
        </div>
      )}

      {pinnedModules.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.text3, margin: '0 0 8px' }}>Pinned</h2>
          {pinError && <p role="alert" style={{ fontSize: 12.5, color: '#ef4444', margin: '0 0 8px' }}>{pinError}</p>}
          <div className="cxd-pins">
            {pinnedModules.map(m => (
              <div key={m.key} className="cxd-pin" role="link" tabIndex={0} aria-label={`Open ${m.label}`} title={`Open ${m.label} in a new tab`}
                onClick={() => window.open(m.url, '_blank')}
                onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); window.open(m.url, '_blank'); } }}
                style={{ ...card, borderRadius: 14, padding: '10px 10px 10px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minWidth: 0 }}>
                <span style={{ width: 34, height: 34, borderRadius: 10, flex: 'none', background: darkMode ? 'rgba(255,255,255,0.06)' : T.bg3, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e8620a' }}>
                  <TileIcon d={m.icon} size={17} />
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</span>
                <button type="button" title="Unpin from Dashboard" aria-label={`Unpin ${m.label} from Dashboard`}
                  onClick={e => { e.stopPropagation(); unpin(m.key); }}
                  style={{ width: 28, height: 28, flex: 'none', borderRadius: 8, padding: 0, border: 'none', background: 'rgba(232,98,10,0.12)', color: '#e8620a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PinIcon filled size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div style={{ ...card, borderRadius: 16, padding: '40px 20px', textAlign: 'center', color: T.text3, fontSize: 13.5 }}>
          No widgets on your dashboard. Use “Add widget” to bring some back.
        </div>
      ) : (
        <div className="cxd-grid">
          {visible.map(k => {
            const w = byKey[k], Comp = WIDGET_COMPONENTS[k];
            if (!Comp) return null;
            return (
              <section key={k} className={`cxd-w cxd-s${sizeOf(k)}${overKey === k ? ' cxd-over' : ''}`} style={card}
                draggable={editing}
                onDragStart={e => { dragKey.current = k; e.dataTransfer.effectAllowed = 'move'; }}
                onDragEnd={() => { dragKey.current = null; setOverKey(null); }}
                onDragOver={e => { if (editing && dragKey.current) { e.preventDefault(); setOverKey(k); } }}
                onDrop={e => { e.preventDefault(); setOrder(dragKey.current, k); setOverKey(null); dragKey.current = null; }}>
                <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px 2px' }}>
                  {editing && <span style={{ color: T.text3, cursor: 'grab', letterSpacing: -2, fontSize: 15, userSelect: 'none' }}>⋮⋮</span>}
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text }}>{w.label}</h3>
                  {w.scope && <span style={{ fontSize: 10.5, fontWeight: 600, color: T.text3, background: T.bg3, borderRadius: 20, padding: '2px 8px' }}>{w.scope}</span>}
                  {editing && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      {w.sizes.length > 1 && <button onClick={() => cycleSize(k)} style={{ border: `1px solid ${T.border}`, background: T.cardBg, color: T.text3, borderRadius: 7, fontSize: 11, padding: '2px 7px', cursor: 'pointer' }}>S / M / L</button>}
                      <button onClick={() => hide(k)} title="Hide widget" style={{ border: '1px solid rgba(239,68,68,0.4)', background: T.cardBg, color: '#ef4444', borderRadius: 7, fontSize: 11, padding: '2px 7px', cursor: 'pointer' }}>✕</button>
                    </span>
                  )}
                </header>
                <div style={{ padding: '8px 16px 16px', pointerEvents: editing ? 'none' : 'auto' }}>
                  <Comp T={T} darkMode={darkMode} go={setActivePage} role={role} />
                </div>
              </section>
            );
          })}
        </div>
      )}

      {drawer && (
        <>
          <div onClick={() => setDrawer(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }} />
          <aside className="cxd-drawer" style={{ background: T.cardBg }}>
            <div style={{ padding: '18px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: 16, color: T.text }}>Widget library</h2>
              <button style={btn(false)} onClick={() => setDrawer(false)}>Close</button>
            </div>
            <div style={{ padding: '6px 20px 20px', overflowY: 'auto', flex: 1 }}>
              <h4 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.text3, margin: '16px 0 8px' }}>Available to add ({hiddenAllowed.length})</h4>
              {hiddenAllowed.length === 0 && <p style={{ fontSize: 12.5, color: T.text3 }}>Every widget you can use is already on your dashboard.</p>}
              {hiddenAllowed.map(k => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{byKey[k].label}</div><div style={{ fontSize: 11.5, color: T.text3 }}>{byKey[k].description}</div></div>
                  <button style={btn(true)} onClick={() => { show(k); persist({ ...draft, hidden: draft.hidden.filter(x => x !== k) }); }}>Add</button>
                </div>
              ))}
              {locked.length > 0 && (
                <>
                  <h4 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: T.text3, margin: '18px 0 8px' }}>Not available for your role ({locked.length})</h4>
                  {locked.map(w => (
                    <div key={w.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 8, opacity: 0.55 }}>
                      <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{w.label}</div><div style={{ fontSize: 11.5, color: T.text3 }}>{w.description}</div></div>
                      <span title="Ask your admin for access">🔒</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
